/**
 * Share an image to the OS share sheet.
 *
 * On native (Capacitor Android) we persist the PNG to the cache directory and
 * hand its file URI to @capacitor/share. On the web we use the Web Share API
 * with a File where available, falling back to a plain download.
 */

const SHARE_TITLE = 'My Chortle day';

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      // strip the "data:image/png;base64," prefix
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/** Triggers a browser download as a last-resort fallback. */
function download(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * @returns `true` if the share sheet (or download fallback) ran, `false` if the
 * user cancelled. Throws only on unexpected failures.
 */
export async function shareImageBlob(blob: Blob, filename: string, text: string): Promise<boolean> {
  if (__NATIVE__) {
    const [{ Filesystem, Directory }, { Share }] = await Promise.all([
      import('@capacitor/filesystem'),
      import('@capacitor/share'),
    ]);
    const data = await blobToBase64(blob);
    await Filesystem.writeFile({ path: filename, data, directory: Directory.Cache });
    const { uri } = await Filesystem.getUri({ path: filename, directory: Directory.Cache });
    try {
      await Share.share({ title: SHARE_TITLE, text, files: [uri] });
      return true;
    } catch (err) {
      if (err instanceof Error && /cancel/i.test(err.message)) return false;
      throw err;
    }
  }

  // Web: prefer native file share, else download.
  const file = new File([blob], filename, { type: 'image/png' });
  const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
  if (typeof nav.share === 'function' && nav.canShare?.({ files: [file] })) {
    try {
      await nav.share({ files: [file], text, title: SHARE_TITLE });
      return true;
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return false;
      // Fall through to download on other share errors.
    }
  }
  download(blob, filename);
  return true;
}
